import type { AirlineMode, AirlinePreference, Assumptions, Cabin } from "./types";

/** Route of the estimator page. */
export const ESTIMATOR_PATH = "/dashboard/estimate";

/** Chat route handler for the estimator rail. */
export const ESTIMATOR_CHAT_API_PATH = "/api/enterprise/estimate/chat";

/** Most cities a comparison holds. */
export const MAX_DESTINATIONS = 5;

/** Largest headcount one origin can carry. Matches the fare pipeline's own bound. */
export const MAX_ORIGIN_COUNT = 10_000;

/** Venues a city needs before its rates count as real data instead of noise. */
export const MIN_VENUES_FOR_RATES = 3;

/** Rows read per city when deriving hotel rates. */
export const HOTEL_RATE_ROW_LIMIT = 2000;

/** Starting per-person assumptions, USD. Editable in the table and by the chat. */
export const DEFAULT_ASSUMPTIONS: Assumptions = {
  meetingSpacePerDay: { low: 25, high: 45 },
  mealsPerDay: { low: 90, high: 130 },
};

/**
 * The GSA standard CONUS meals and incidentals (M&IE) rate for FY2027, USD a day. A city's cost
 * index is its own M&IE rate over this one, times {@link COST_INDEX_BASE}, so a place on the
 * standard rate scores 100.
 */
export const STANDARD_CONUS_MIE = 68;

/** The cost index a place on the standard rate scores; the default rates are priced at it. */
export const COST_INDEX_BASE = 100;

/** Where the US per diem rates come from, as the basis names it. */
export const GSA_PER_DIEM_SOURCE = "GSA per diem rates, FY2027";

/** Where the foreign per diem rates come from, as the basis names it. */
export const STATE_PER_DIEM_SOURCE = "US State Department foreign per diem rates, November 2026";

/** Cabin options shown in the workspace, in the fare pipeline's naming. */
export const CABIN_OPTIONS: ReadonlyArray<{ value: Cabin; label: string }> = [
  { value: "economy", label: "Economy" },
  { value: "premium economy", label: "Premium economy" },
  { value: "business", label: "Business" },
  { value: "first", label: "First" },
];

/**
 * Team name given to a city's travelers who fly another cabin than the rest of that city ("5 of
 * the San Diego people in first"), so they stay their own group instead of merging into one cabin.
 */
export const CABIN_GROUP_NAMES: Record<Cabin, string> = {
  economy: "Economy",
  "premium economy": "Premium economy",
  business: "Business class",
  first: "First class",
};

/** Display name of each cabin, e.g. "First". */
export const CABIN_LABELS = Object.fromEntries(CABIN_OPTIONS.map((option) => [option.value, option.label])) as Record<
  Cabin,
  string
>;

/** No airline preference: every airline except the ones the fare pipeline always leaves out. */
export const ANY_AIRLINES: AirlinePreference = { mode: "any", codes: [], names: {} };

/** Airline modes in the trip options popover, in display order. */
export const AIRLINE_MODE_OPTIONS: ReadonlyArray<{ value: AirlineMode; label: string }> = [
  { value: "any", label: "Any" },
  { value: "only", label: "Only these" },
  { value: "exclude", label: "Not these" },
];

/** Most airlines a planner can pick. */
export const MAX_AIRLINE_PICKS = 10;

/** Airline search results shown per query. */
export const AIRLINE_SEARCH_LIMIT = 8;

/** Picked airlines the trip bar names before it shows a count instead. */
export const AIRLINE_LABEL_MAX_NAMES = 2;

/** A two-character IATA airline code, e.g. "UA" or "B6". */
export const AIRLINE_CODE_PATTERN = /^[A-Z0-9]{2}$/;

/** Events offered as starting points on the start screen. */
export const MAX_EVENT_STARTS = 12;

/** Model the estimator chat runs on. Same model the builder copilot uses. */
export const ESTIMATOR_MODEL_ID = "claude-sonnet-5";

/** Effort for the estimator chat; edits are simple, so medium keeps replies quick. */
export const ESTIMATOR_MODEL_EFFORT = "medium";

/**
 * Effort for a turn that continues after the browser applied a tool: the plan is already made,
 * so this step mostly reports what changed, and lower effort answers sooner.
 */
export const ESTIMATOR_FOLLOW_UP_EFFORT = "low";

/** Output cap per chat turn. */
export const ESTIMATOR_MAX_OUTPUT_TOKENS = 4096;

/** Longest conversation the chat route accepts, counting tool round trips. */
export const ESTIMATOR_MAX_HISTORY_MESSAGES = 80;

/** Longest user message, in characters. */
export const ESTIMATOR_MAX_MESSAGE_LENGTH = 4000;

/** Longest workspace summary the client may send the chat route, in characters. */
export const ESTIMATOR_MAX_SUMMARY_LENGTH = 12_000;

/** Chat route rate limit per user. */
export const ESTIMATOR_CHAT_RATE_LIMIT = { windowMs: 60_000, maxCalls: 30 } as const;

/** Flight searches per user: one search prices every group to every destination for one set of dates. */
export const ESTIMATOR_SEARCH_RATE_LIMIT = { windowMs: 10 * 60_000, maxCalls: 30 } as const;

/** Routes (home city to destination) priced per user, across searches, so date-window and city comparisons stay bounded. */
export const ESTIMATOR_ROUTE_RATE_LIMIT = { windowMs: 10 * 60_000, maxCalls: 400 } as const;

/** Hotel-rate lookups per user. */
export const ESTIMATOR_HOTEL_RATE_LIMIT = { windowMs: 10 * 60_000, maxCalls: 200 } as const;

/** Keepalive saves per user (sent as a tab closes). */
export const ESTIMATOR_SAVE_RATE_LIMIT = { windowMs: 60_000, maxCalls: 60 } as const;

/** Longest trip a fare search accepts, in nights. */
export const MAX_SEARCH_NIGHTS = 30;

/**
 * How far ahead a fare search's trip may start, in days. Airlines publish schedules and fares
 * about 11 months out; past that, a search returns nothing for every route, so none is run.
 */
export const MAX_SEARCH_DAYS_AHEAD = 330;

/** Pause after an edit before a new flight search starts, so a burst of edits runs one search. */
export const SEARCH_DEBOUNCE_MS = 700;

/** How often a running flight search is polled. */
export const SEARCH_POLL_MS = 2500;

/** Each poll waits this much longer than the last, up to {@link SEARCH_POLL_MAX_MS}. */
export const SEARCH_POLL_BACKOFF = 1.3;

/** Longest wait between polls of a running flight search. */
export const SEARCH_POLL_MAX_MS = 8000;

/**
 * Longest a flight search is waited on before showing what landed. Matches the estimate pages'
 * `maxDuration` (300s), the longest the server keeps a search running, so fares the server still
 * finds are never written off as missing.
 */
export const SEARCH_TIMEOUT_MS = 300_000;

/** Pause before a search refused for too many searches is tried again. */
export const SEARCH_RATE_LIMIT_RETRY_MS = 60_000;

/** How many times a refused search is retried before it is given up as rate limited. */
export const SEARCH_RATE_LIMIT_RETRIES = 5;

/** How long `read_estimate` waits for fares and hotel rates to settle before answering. */
export const READ_ESTIMATE_TIMEOUT_MS = 60_000;

/** Poll interval while `read_estimate` waits. */
export const READ_ESTIMATE_POLL_MS = 400;

/** Tool calls one assistant turn may make before the client stops resubmitting. */
export const MAX_TOOL_CALLS_PER_TURN = 16;

/** Largest chat request body the route accepts, in characters. */
export const ESTIMATOR_MAX_REQUEST_CHARS = 300_000;

/** Max date options or follow-up replies the chat may offer at once. */
export const MAX_REPLY_SUGGESTIONS = 3;

/** Max nearby-city suggestions the chat may offer at once. */
export const MAX_CITY_SUGGESTIONS = 4;

/** Label column of the comparison table with one or two cities, in pixels. */
export const TABLE_LABEL_WIDE_PX = 260;

/** Label column of the comparison table with three or more cities, in pixels. */
export const TABLE_LABEL_PX = 200;

/** Label column of the comparison table in a column narrower than {@link TABLE_ROOMY_PX}, in pixels. */
export const TABLE_LABEL_COMPACT_PX = 184;

/** Column width from which the table uses its wider label column. */
export const TABLE_ROOMY_PX = 760;

/** Date windows the chat may price in one go. */
export const MAX_DATE_WINDOWS = 4;

/** Destinations priced per date window. */
export const MAX_WINDOW_DESTINATIONS = 3;

/** Candidate cities the chat may price in one go. */
export const MAX_CANDIDATE_CITIES = 5;

/** Home-city fare rows shown before "show all". */
export const ROUTE_ROWS_SHOWN = 6;

/** Title of an estimate with nothing to name it by yet. */
export const DEFAULT_ESTIMATE_TITLE = "New estimate";

/** Base path of the estimator; an estimate lives at `${ESTIMATES_PATH}/<id>`. */
export const ESTIMATES_PATH = "/dashboard/estimate";

/** How a note posted from an estimate dates itself: "As of Oct 24, 2026". */
export const NOTE_DATE_FORMAT = "MMM D, YYYY";

/** Opens every note posted from an estimate, before its date. */
export const NOTE_AS_OF_LABEL = "As of";

/** Closes every note posted from a saved estimate, before the link to it. */
export const NOTE_LIVE_LINK_LABEL = "Open the live estimate:";

/** Prefix of the notes thread an estimate posts to; its title follows. */
export const NOTE_THREAD_TITLE_PREFIX = "Cost & flight estimator:";

/** Tries at starting an estimate's notes thread: a post that loses the race posts into the winner's. */
export const NOTE_THREAD_CLAIM_ATTEMPTS = 2;

/** Quiet time after the last change before an estimate is saved. */
export const AUTOSAVE_DEBOUNCE_MS = 1200;

/** A failed autosave retries after this long, doubling each time up to {@link AUTOSAVE_RETRY_MAX_MS}. */
export const AUTOSAVE_RETRY_BASE_MS = 2000;

/** The longest wait between autosave retries. */
export const AUTOSAVE_RETRY_MAX_MS = 30_000;

/**
 * Browsers cap keepalive bodies at 64 KiB (counted in bytes, shared by the page's pending keepalive
 * requests); a larger unsaved change asks the planner to stay instead.
 */
export const AUTOSAVE_KEEPALIVE_MAX_BYTES = 60_000;

/** The route a closing tab sends its last unsaved change to. */
export const ESTIMATE_SAVE_ROUTE = "/api/enterprise/estimate/save";

/** Saved estimates loaded into the list. */
export const SAVED_ESTIMATES_LIMIT = 200;

/** Server reads of an estimate the browser remembers having used, to ignore router-cache replays. */
export const PRIMED_READS_REMEMBERED = 50;

/** Longest estimate title, in characters. */
export const MAX_TITLE_LENGTH = 120;

/** Largest estimate a save may send, in characters of JSON; server actions cap the body at 1 MB. */
export const MAX_SAVED_ESTIMATE_CHARS = 900_000;

/** DOM id of the trip bar's When segment, so the "add dates" prompt can open it. */
export const DATES_TRIGGER_ID = "estimate-dates-trigger";

/** Id of the trip bar's Who segment, so a notice can open it. */
export const WHO_TRIGGER_ID = "estimate-who-trigger";

/** Saved estimates whose dates the list reads, as a PostgREST JSON path. */
export const SAVED_SUMMARY_DATES_COLUMN = "dates:workspace->dates";

/** Venue quotes from an event's proposals the estimate and the assistant read. */
export const MAX_EVENT_PROPOSALS = 12;

/** Search param that opens the estimator for an event: `/dashboard/estimate?event=<id>`. */
export const EVENT_PARAM = "event";

/** MIME type of the Excel export. */
export const XLSX_MIME_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/** The exports' one accent colour (indigo-600), as a hex without `#`. */
export const EXPORT_ACCENT = "4F46E5";

/** Font the PowerPoint export asks for; PowerPoint falls back to its default when it's missing. */
export const EXPORT_FONT_FACE = "Arial";

/** Home-city flights listed per city slide in the PowerPoint export. */
export const EXPORT_FLIGHTS_PER_SLIDE = 6;

/** Saved estimates listed on the home before "Show all". */
export const RECENT_ROWS_SHOWN = 5;

/** Events offered as chips under a blank trip bar; the rest sit in "More events". */
export const EVENT_CHIPS_SHOWN = 4;

/** Airport rows read per page when building the place index. */
export const AIRPORT_INDEX_PAGE = 1000;

/** Frequent home cities offered when the Who picker opens empty. */
export const QUICK_PICKS_SHOWN = 6;

/** Saved estimates read to find the org's frequent home cities. */
export const QUICK_PICK_ESTIMATES_READ = 50;

/** Places offered in the picker per search. */
export const PLACE_OPTIONS_SHOWN = 6;

/** Column width below which the trip bar stacks into a card; matches its `@[520px]` breakpoint. */
export const TRIP_BAR_STACK_PX = 520;

/** Browser storage key for whether the planner keeps the assistant open. */
export const ASSISTANT_OPEN_STORAGE_KEY = "cost-estimator:assistant-open";

/** Viewport width (Tailwind `lg`) from which the assistant docks as a rail instead of a sheet. */
export const ASSISTANT_RAIL_MIN_WIDTH = 1024;

/** Trip bar width from which the cabin and currency control spells the cabin out ("Economy · USD"). */
export const TRIP_BAR_ROOMY_PX = 680;

/** Column width below which the table's label column narrows further, in pixels. */
export const TABLE_SNUG_PX = 520;

/**
 * Label column of the comparison table in a column narrower than {@link TABLE_SNUG_PX}, in pixels:
 * enough for "San Francisco" and "200 people · SFO" on one line each. City columns give way first.
 */
export const TABLE_LABEL_SNUG_PX = 156;

/** Narrowest a city column gets before the table scrolls sideways instead, in pixels. */
export const TABLE_MIN_CITY_PX = 104;

/** City column width below which the table goes dense: tighter padding, smaller totals, shorter captions. */
export const TABLE_DENSE_COLUMN_PX = 170;

/** Width of the comparison table's "Add city" column, in pixels. */
export const ADD_CITY_COLUMN_PX = 124;

/** Width of the fade at a scrolling chip row's edge, in pixels. */
export const CHIP_ROW_FADE_PX = 48;

/** Share of a chip row's visible width one arrow press scrolls. */
export const CHIP_ROW_SCROLL_SHARE = 0.8;

/** How close (0 to 1) an assistant's misspelled city must be to resolve on its own. */
export const ASSISTANT_TYPO_MIN = 0.85;

/** How far ahead of the next match a typo match must be to count as the one meant. */
export const TYPO_LEAD = 0.1;

/** The blank assistant's example of finding a place by kind rather than by name. */
export const STARTER_DISCOVERY = "Price a warm-weather offsite for 40 people in March";

/** Distinct values per column checked as places when an attendee sheet has no header. */
export const ATTENDEE_COLUMN_SAMPLE = 20;

/** Share of a column's values that must be places before an attendee sheet is read as having home cities. */
export const MIN_PLACE_SHARE = 0.2;

/** Rows of an attendee list shown to the assistant as they are, for the list's shape. */
export const ROSTER_SAMPLE_ROWS = 5;

/** Distinct values per column the assistant sees, with their counts. */
export const ROSTER_MAX_VALUES_PER_COLUMN = 300;

/** Rows a list needs before a column of distinct values reads as one value per person. */
export const ROSTER_ID_COLUMN_MIN_ROWS = 20;

/** Share of distinct values that marks a column as one value per person (names, emails). */
export const ROSTER_ID_COLUMN_SHARE = 0.9;

/** Values shown from a one-value-per-person column; enough to tell what it holds. */
export const ROSTER_ID_COLUMN_VALUES = 10;

/** Most characters of an attendee list sent to the assistant with each message. */
export const MAX_ROSTER_CHARS = 60_000;

/** Largest attendee file read in the browser; bigger ones are refused before parsing. */
export const MAX_ATTENDEE_FILE_BYTES = 10 * 1024 * 1024;

/** Rows read from an attendee sheet; anything below is ignored, so a huge sheet can't stall the tab. */
export const MAX_ATTENDEE_ROWS = 20_000;

/** Amounts at or above this, in the display currency, show compact ("¥1.9M") so they fit a column. */
export const COMPACT_AMOUNT_FROM = 100_000;

/** How close to the bottom, in px, the assistant thread still counts as following new content. */
export const CHAT_PIN_SLACK_PX = 48;

/**
 * Version of the airport catalog's shape, in its cache key and the browser's copy. Bump it when
 * the fields of an airport record change, so no browser or server reads a stale shape.
 */
export const AIRPORT_CATALOG_VERSION = 1;

/** How long the airport catalog is reused before it is read again, in seconds (a day). */
export const AIRPORT_CATALOG_REVALIDATE_S = 24 * 60 * 60;

/** Where the browser loads the airport catalog from. */
export const AIRPORT_CATALOG_ROUTE = "/api/enterprise/estimate/airports";

/** Placeholder rows the airport picker shows while the catalog loads, shaped like its airport rows. */
export const AIRPORT_PICKER_SKELETON_ROWS = 2;

/** How long a city's venue hotel rates are reused, in seconds, on the server and in the browser. */
export const HOTEL_RATE_CACHE_S = 60 * 60;

/** How long the org's frequent home cities are reused in the browser, in ms. */
export const FREQUENT_CITIES_TTL_MS = 5 * 60_000;

/** Fares are only searched for trips starting at least this many days from today. */
export const MIN_FARE_LEAD_DAYS = 3;

/** Venues listed as examples behind a city's hotel range. */
export const VENUE_SAMPLES_SHOWN = 5;

/** From this many cities the comparison opens as the ranked list; fewer compare side by side. */
export const SUMMARY_MIN_CITIES = 4;

/** Most cities compared side by side at once. */
export const COMPARE_MAX_CITIES = 3;

/** Query parameter naming the city whose breakdown is open in the summary list. */
export const CITY_PARAM = "city";

/** What "Estimate this event" asks the assistant. */
export const ESTIMATE_EVENT_PROMPT =
  "Estimate this event: price every city for the current travelers and dates, then tell me the cheapest and the most expensive per person and in total, and what's still missing.";

/** Most characters of the event block the assistant reads. */
export const MAX_EVENT_CONTEXT_CHARS = 6000;

/** Most characters of the organization block (teams and events) the assistant reads. */
export const MAX_ORG_CONTEXT_CHARS = 6000;

/** Home cities the assistant reads by name; the rest are summed into one line per city. */
export const MODEL_HOME_CITIES_LISTED = 8;

/** Width the trip bar's spelled-out "Estimate" button takes beyond an icon-only one; the cabin control shortens that much sooner. */
export const ESTIMATE_LABEL_PX = 72;

/** Fewest priced categories a city needs before its breakdown shows cost bars. */
export const MIN_COST_BARS = 2;

/** The public sample estimate's name. */
export const SAMPLE_TITLE = "Team offsite";

/** Days from today to the public sample's earliest start, inside the airlines' publish window. */
export const SAMPLE_LEAD_DAYS = 42;

/** Weekday the public sample starts on (0 is Sunday): a Tuesday. */
export const SAMPLE_START_WEEKDAY = 2;

/** Nights in the public sample trip. */
export const SAMPLE_NIGHTS = 3;

/** Venues behind the public sample's first city's hotel range; each next city has a few more. */
export const SAMPLE_VENUE_COUNT_FLOOR = 48;

/** `metadata.source` on a lead from the public estimator. */
export const PUBLIC_ESTIMATOR_LEAD_SOURCE = "cost-estimator";
